import { AdditionalParams, Data } from "@shared/platform-api/requests"

export const findFeSortKey = <SK, FESK>(sortKey?: SK, sortKeyMap?: Map<FESK, SK>) => {
  if (!sortKeyMap || !sortKey) return undefined
  const mayBeSortKeyEntry = [...sortKeyMap.entries()].find(({ 1: sk }) => sk === sortKey)
  if (!mayBeSortKeyEntry) return undefined
  const [feSortKey] = mayBeSortKeyEntry
  return feSortKey
}

export type PaginatedApiState<T, FESK> = {
  pages: Array<ReadonlyArray<T>>
  currentPage?: ReadonlyArray<T>
  pageIndex: number
  pageCount: number
  dataCount: number
  prevDisabled: boolean
  nextDisabled: boolean
  next: string | null
  sortProperty?: FESK
}

export type PaginatedApiAction<T, FESK> =
  | { type: `refresh`; data?: Data<T>; pageSize: number }
  | { type: `addApiPage`; value: Data<T> }
  | { type: `setSortProperty`; value?: FESK }
  | { type: `forwards` }
  | { type: `backwards` }

export const makeInitialState = <T, I, SK, FESK>(
  input?: I & AdditionalParams<SK>,
  sortKeyMap?: Map<FESK, SK>
): PaginatedApiState<T, FESK> => ({
  pages: [],
  pageIndex: 0,
  pageCount: 1,
  dataCount: 0,
  prevDisabled: true,
  nextDisabled: true,
  next: null,
  sortProperty: findFeSortKey(input?.sortKey, sortKeyMap),
})

const addApiPage = <T>(data: Data<T>, currentPages: Array<ReadonlyArray<T>>, pageCount: number) => {
  const { items, next } = data
  const pages = [...currentPages]
  pages.push(items)
  return {
    pages,
    pageIndex: pages.length - 1,
    currentPage: items,
    nextDisabled: pages.length === pageCount,
    prevDisabled: false,
    next,
  }
}

const onRefresh = <T, I, SK, FESK>(
  pageSize: number,
  data?: Data<T>,
  sortProperty?: FESK
): PaginatedApiState<T, FESK> => {
  if (!data) return makeInitialState<T, I, SK, FESK>()
  const { items, total, next } = data
  const pageCount = Math.ceil(total / pageSize)
  return {
    pages: [items],
    currentPage: items,
    pageIndex: 0,
    dataCount: data.total,
    pageCount,
    prevDisabled: true,
    nextDisabled: pageCount === 1,
    next,
    sortProperty,
  }
}

export const makePaginatedApiReducer = <T, FESK>() =>
  function (state: PaginatedApiState<T, FESK>, action: PaginatedApiAction<T, FESK>) {
    return paginatedApiReducer(state, action)
  }

export const paginatedApiReducer = <T, FESK>(
  state: PaginatedApiState<T, FESK>,
  action: PaginatedApiAction<T, FESK>
): PaginatedApiState<T, FESK> => {
  switch (action.type) {
    case `refresh`:
      return onRefresh(action.pageSize, action.data, state.sortProperty)
    case `addApiPage`:
      return {
        ...state,
        ...addApiPage(action.value, state.pages, state.pageCount),
      }
    case `setSortProperty`:
      return {
        ...state,
        sortProperty: action.value,
      }
    case `forwards`:
      return {
        ...state,
        pageIndex: state.pageIndex + 1,
        currentPage: state.pages[state.pageIndex + 1],
        nextDisabled: state.pageIndex + 1 === state.pageCount - 1,
        prevDisabled: false,
      }
    case `backwards`:
      return {
        ...state,
        pageIndex: state.pageIndex - 1,
        currentPage: state.pages[state.pageIndex - 1],
        nextDisabled: false,
        prevDisabled: state.pageIndex - 1 === 0,
      }
    default:
      return state
  }
}
